import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ClipboardCheck, Search, Plus, FileText, CheckCircle2, AlertTriangle, XCircle,
  Building2, Calendar, Users, ChevronRight, Eye, Edit, Trash2, Download,
  RefreshCw, Target, Shield, TrendingUp, BarChart3, Sparkles, Filter,
  Clock, Play, Loader2, CalendarClock
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function GapAnalysisPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [mainTab, setMainTab] = useState('assessments');
  
  // Automated assessments
  const [automatedReports, setAutomatedReports] = useState([]);
  const [loadingAutomated, setLoadingAutomated] = useState(false);
  const [runningAssessment, setRunningAssessment] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState('');
  
  // New analysis form
  const [newAnalysis, setNewAnalysis] = useState({
    title: '',
    description: '',
    framework: 'icao_annex_19',
    assessment_type: 'comprehensive',
    departments: [],
    aircraft_types: []
  });

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/stats`, { headers: apiHeaders });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchAnalyses = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/gap-analysis?limit=50`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (frameworkFilter) url += `&framework=${frameworkFilter}`;
      if (searchQuery) url += `&search=${searchQuery}`;
      
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAnalyses(data.analyses || []);
      }
    } catch (err) {
      console.error('Error fetching analyses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFrameworks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/frameworks`, { headers: apiHeaders });
      if (res.ok) setFrameworks(await res.json());
    } catch (err) {
      console.error('Error fetching frameworks:', err);
    }
  };

  const fetchAnalysisDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/${id}`, { headers: apiHeaders });
      if (res.ok) {
        setSelectedAnalysis(await res.json());
        setShowDetailDialog(true);
      }
    } catch (err) {
      toast.error('Error fetching analysis details');
    }
  };

  const createAnalysis = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(newAnalysis)
      });
      if (res.ok) {
        toast.success('Gap analysis created');
        setShowCreateDialog(false);
        setNewAnalysis({ title: '', description: '', framework: 'icao_annex_19', assessment_type: 'comprehensive', departments: [], aircraft_types: [] });
        fetchAnalyses();
        fetchStats();
      } else {
        toast.error('Failed to create analysis');
      }
    } catch (err) {
      toast.error('Error creating analysis');
    }
  };

  const updateRequirement = async (analysisId, requirementId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/${analysisId}/requirements/${requirementId}`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ compliance_status: status })
      });
      if (res.ok) {
        toast.success('Requirement updated');
        fetchAnalysisDetail(analysisId);
      }
    } catch (err) {
      toast.error('Error updating requirement');
    }
  };

  const seedSample = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/seed`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Sample analysis created');
        fetchAnalyses();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error seeding sample');
    }
  };

  const fetchAutomatedReports = async () => {
    setLoadingAutomated(true);
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/automated-assessments?limit=20`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAutomatedReports(data.reports || []);
      }
    } catch (err) {
      console.error('Error fetching automated reports:', err);
    } finally {
      setLoadingAutomated(false);
    }
  };

  const triggerAutomatedAssessment = async () => {
    setRunningAssessment(true);
    try {
      const res = await fetch(`${API_URL}/api/gap-analysis/trigger-automated-assessment`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Assessment completed');
        fetchAutomatedReports();
        fetchStats();
      } else {
        toast.error('Failed to run assessment');
      }
    } catch (err) {
      toast.error('Error running assessment');
    } finally {
      setRunningAssessment(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAnalyses();
    fetchFrameworks();
    fetchAutomatedReports();
  }, []);

  useEffect(() => {
    fetchAnalyses();
  }, [statusFilter, frameworkFilter, searchQuery]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
      in_progress: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      under_review: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      completed: 'bg-green-500/20 text-green-300 border-green-500/30',
      archived: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };
    return styles[status] || styles.draft;
  };

  const getComplianceColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getComplianceStatusIcon = (status) => {
    switch (status) {
      case 'compliant': return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'partially_compliant': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'non_compliant': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <div className="w-4 h-4 rounded-full bg-slate-600" />;
    }
  };

  const groupRequirementsByPillar = (requirements) => {
    const grouped = {};
    for (const req of requirements || []) {
      const pillar = req.pillar || 'other';
      if (!grouped[pillar]) grouped[pillar] = [];
      grouped[pillar].push(req);
    }
    return grouped;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6" data-testid="gap-analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ClipboardCheck className="w-8 h-8 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-100">{t('gapAnalysis.title')}</h1>
            <p className="text-sm text-slate-400">{t('gapAnalysis.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={seedSample}>
            <Sparkles className="w-4 h-4 mr-2" />
            Seed Sample
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-gap-analysis-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('gapAnalysis.newAssessment')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('gapAnalysis.assessments')}</p>
                <p className="text-2xl font-bold text-slate-100">{stats?.total_assessments || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('common.inProgress')}</p>
                <p className="text-2xl font-bold text-blue-400">{stats?.in_progress || 0}</p>
              </div>
              <RefreshCw className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('common.completed')}</p>
                <p className="text-2xl font-bold text-green-400">{stats?.completed || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">{t('common.average')}</p>
                <p className={`text-2xl font-bold ${getComplianceColor(stats?.average_compliance_score || 0)}`}>
                  {stats?.average_compliance_score?.toFixed(1) || 0}%
                </p>
              </div>
              <Target className="w-8 h-8 text-emerald-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Frameworks</p>
                <p className="text-2xl font-bold text-purple-400">{frameworks?.length || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-6">
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="assessments" data-testid="manual-assessments-tab">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            Manual Assessments
          </TabsTrigger>
          <TabsTrigger value="automated" data-testid="automated-assessments-tab">
            <CalendarClock className="w-4 h-4 mr-2" />
            Automated (Quarterly)
          </TabsTrigger>
        </TabsList>

        {/* Manual Assessments Tab */}
        <TabsContent value="assessments">
          {/* Filters */}
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search assessments..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-900/50"
                  />
                </div>
                <Select value={frameworkFilter || 'all'} onValueChange={(v) => setFrameworkFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[200px] bg-slate-900/50">
                    <SelectValue placeholder="Framework" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Frameworks</SelectItem>
                    {frameworks?.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px] bg-slate-900/50">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => { setSearchQuery(''); setStatusFilter(''); setFrameworkFilter(''); }}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Analyses List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-2 text-center py-12 text-slate-500">Loading...</div>
            ) : analyses.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-slate-500">
                No gap analyses found. Create one or seed sample data.
              </div>
            ) : (
              analyses.map(analysis => (
                <Card 
                  key={analysis.id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-emerald-500/50 transition-all cursor-pointer"
                  onClick={() => fetchAnalysisDetail(analysis.id)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <Badge className={getStatusBadge(analysis.status)}>{analysis.status?.replace('_', ' ')}</Badge>
                        <Badge variant="outline" className="ml-2">{analysis.framework?.replace(/_/g, ' ').toUpperCase()}</Badge>
                      </div>
                      <span className="text-xs text-slate-500">{analysis.reference_number}</span>
                    </div>
                    <h3 className="font-semibold text-slate-100 mb-2">{analysis.title}</h3>
                    <p className="text-sm text-slate-400 line-clamp-2 mb-4">{analysis.description}</p>
                    
                    {/* Compliance Score */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-400">Compliance Score</span>
                        <span className={`font-bold ${getComplianceColor(analysis.overall_compliance_score || 0)}`}>
                          {analysis.overall_compliance_score?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <Progress value={analysis.overall_compliance_score || 0} className="h-2" />
                    </div>

                    {/* Counts */}
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-400" />
                        {analysis.compliant_count || 0} Compliant
                      </span>
                      <span className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-yellow-400" />
                        {analysis.partial_count || 0} Partial
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3 h-3 text-red-400" />
                        {analysis.non_compliant_count || 0} Gaps
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Automated Assessments Tab */}
        <TabsContent value="automated">
          <Card className="bg-slate-800/50 border-slate-700 mb-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-purple-400" />
                    Quarterly Automated Assessments
                  </CardTitle>
                  <CardDescription>
                    Automated GAP assessments run quarterly (Q1, Q2, Q3, Q4) to track compliance trends
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={fetchAutomatedReports} disabled={loadingAutomated}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingAutomated ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button onClick={triggerAutomatedAssessment} disabled={runningAssessment} data-testid="trigger-assessment-btn">
                    {runningAssessment ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Run Now
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Info Banner */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-purple-300">Scheduled Automation</h4>
                    <p className="text-sm text-slate-400 mt-1">
                      Automated assessments run on the 1st of January, April, July, and October at 3:00 AM UTC.
                      They aggregate compliance data from all manual assessments and generate quarterly reports.
                    </p>
                  </div>
                </div>
              </div>

              {/* Reports List */}
              {loadingAutomated ? (
                <div className="text-center py-12 text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  Loading reports...
                </div>
              ) : automatedReports.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarClock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">No automated assessments yet</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Run a manual assessment or wait for the next scheduled run
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {automatedReports.map(report => (
                    <div 
                      key={report.id}
                      className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-purple-500/30 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                            <span className="text-lg font-bold text-purple-400">Q{report.quarter}</span>
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-100">Q{report.quarter} {report.year} Assessment</h3>
                            <p className="text-xs text-slate-500">
                              Generated: {new Date(report.generated_at).toLocaleDateString('en-US', { 
                                year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                              })}
                              {report.triggered_manually && (
                                <Badge variant="outline" className="ml-2 text-xs">Manual Run</Badge>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${getComplianceColor(report.overall_compliance || 0)}`}>
                            {report.overall_compliance?.toFixed(1) || 0}%
                          </p>
                          <p className="text-xs text-slate-500">Overall Compliance</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 mt-4">
                        <div className="text-center p-2 bg-slate-800/50 rounded">
                          <p className="text-lg font-semibold text-slate-100">{report.total_requirements || 0}</p>
                          <p className="text-xs text-slate-500">Requirements</p>
                        </div>
                        <div className="text-center p-2 bg-green-500/10 rounded">
                          <p className="text-lg font-semibold text-green-400">{report.compliant_count || 0}</p>
                          <p className="text-xs text-slate-500">Compliant</p>
                        </div>
                        <div className="text-center p-2 bg-yellow-500/10 rounded">
                          <p className="text-lg font-semibold text-yellow-400">{report.partial_count || 0}</p>
                          <p className="text-xs text-slate-500">Partial</p>
                        </div>
                        <div className="text-center p-2 bg-red-500/10 rounded">
                          <p className="text-lg font-semibold text-red-400">{report.non_compliant_count || 0}</p>
                          <p className="text-xs text-slate-500">Non-Compliant</p>
                        </div>
                      </div>
                      
                      {report.assessments_analyzed && (
                        <p className="text-xs text-slate-500 mt-3">
                          Based on {report.assessments_analyzed} assessment(s) analyzed
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Create Gap Analysis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Title</Label>
              <Input
                value={newAnalysis.title}
                onChange={(e) => setNewAnalysis({ ...newAnalysis, title: e.target.value })}
                placeholder="Annual ICAO SMS Assessment"
                className="bg-slate-800/50"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={newAnalysis.description}
                onChange={(e) => setNewAnalysis({ ...newAnalysis, description: e.target.value })}
                placeholder="Comprehensive gap analysis against..."
                className="bg-slate-800/50"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Regulatory Framework</Label>
              <Select value={newAnalysis.framework} onValueChange={(v) => setNewAnalysis({ ...newAnalysis, framework: v })}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {frameworks?.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name} ({f.total_requirements} reqs)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Assessment Type</Label>
              <Select value={newAnalysis.assessment_type} onValueChange={(v) => setNewAnalysis({ ...newAnalysis, assessment_type: v })}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="comprehensive">Comprehensive</SelectItem>
                  <SelectItem value="targeted">Targeted</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createAnalysis} disabled={!newAnalysis.title || !newAnalysis.framework}>
              Create Assessment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-emerald-400" />
              {selectedAnalysis?.title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAnalysis && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-slate-800/50 mb-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="requirements">Requirements ({selectedAnalysis.total_requirements})</TabsTrigger>
                <TabsTrigger value="gaps">Gaps ({selectedAnalysis.non_compliant_count + selectedAnalysis.partial_count})</TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Framework</p>
                    <p className="text-slate-100">{selectedAnalysis.framework?.replace(/_/g, ' ').toUpperCase()}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400">Status</p>
                    <Badge className={getStatusBadge(selectedAnalysis.status)}>{selectedAnalysis.status?.replace('_', ' ')}</Badge>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-400">Overall Compliance</span>
                    <span className={`text-2xl font-bold ${getComplianceColor(selectedAnalysis.overall_compliance_score || 0)}`}>
                      {selectedAnalysis.overall_compliance_score?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <Progress value={selectedAnalysis.overall_compliance_score || 0} className="h-3" />
                </div>

                {/* Pillar Scores */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300">Scores by Pillar</h4>
                  {Object.entries(selectedAnalysis.pillar_scores || {}).map(([pillar, score]) => (
                    <div key={pillar} className="flex items-center gap-3">
                      <div className="w-40 text-sm text-slate-400">{pillar.replace(/_/g, ' ')}</div>
                      <Progress value={score} className="flex-1 h-2" />
                      <span className={`w-12 text-right ${getComplianceColor(score)}`}>{score.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="requirements">
                <div className="space-y-4">
                  {Object.entries(groupRequirementsByPillar(selectedAnalysis.requirements)).map(([pillar, reqs]) => (
                    <div key={pillar} className="border border-slate-700 rounded-lg p-4">
                      <h4 className="font-medium text-slate-200 mb-3 capitalize">{pillar.replace(/_/g, ' ')}</h4>
                      <div className="space-y-2">
                        {reqs.map(req => (
                          <div key={req.id} className="flex items-start gap-3 p-2 rounded bg-slate-800/50">
                            {getComplianceStatusIcon(req.compliance_status)}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-emerald-400 font-mono">{req.requirement_code}</span>
                                <span className="text-sm text-slate-300">{req.requirement_text}</span>
                              </div>
                              {req.gap_description && (
                                <p className="text-xs text-red-400 mt-1">Gap: {req.gap_description}</p>
                              )}
                            </div>
                            <Select 
                              value={req.compliance_status || 'not_assessed'} 
                              onValueChange={(v) => updateRequirement(selectedAnalysis.id, req.id, v)}
                            >
                              <SelectTrigger className="w-[140px] h-8 text-xs bg-slate-900/50">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="not_assessed">Not Assessed</SelectItem>
                                <SelectItem value="compliant">Compliant</SelectItem>
                                <SelectItem value="partially_compliant">Partial</SelectItem>
                                <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                                <SelectItem value="not_applicable">N/A</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="gaps">
                <div className="space-y-3">
                  {selectedAnalysis.requirements?.filter(r => r.compliance_status === 'non_compliant' || r.compliance_status === 'partially_compliant').map(req => (
                    <Card key={req.id} className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {getComplianceStatusIcon(req.compliance_status)}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-emerald-400 font-mono">{req.requirement_code}</span>
                              <Badge variant="outline" className="text-xs">{req.pillar?.replace(/_/g, ' ')}</Badge>
                            </div>
                            <p className="text-sm text-slate-200">{req.requirement_text}</p>
                            {req.gap_description && (
                              <p className="text-sm text-red-400 mt-2">Gap: {req.gap_description}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {selectedAnalysis.requirements?.filter(r => r.compliance_status === 'non_compliant' || r.compliance_status === 'partially_compliant').length === 0 && (
                    <p className="text-center text-slate-500 py-8">No gaps identified</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
